import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import GlyphSvg from '../components/Glyphs';
import '../utils/how-it-works.css';
import PixelScroll from '../components/PixelScroll';
import ImageGridCanvas from '../components/ImageGrid';
import AnimatedGlyphGrid from '../components/AnimatedGlyphGrid';
import UploadProgressGlyphs from '../components/UploadProgressGlyphs';
import ImageCrossfade from '../components/ImageCrossfade';
import { usePageTitle } from '../hooks/usePageTitle';
import { pageConfig } from '../config/page-titles';

export default function HowItWorks() {
  usePageTitle({
    title: pageConfig.howItWorks.title,
    description: pageConfig.howItWorks.description,
    image: pageConfig.howItWorks.image
  });

  // Create a ref to store all the step elements for observing
  const stepRefs = useRef([]);

  // Function to add a DOM element to the refs array
  const addToRefs = (el) => {
    if (el && !stepRefs.current.includes(el)) {
      stepRefs.current.push(el);
    }
  };

  const scrollToPixelScroll = () => {
    console.log('Scroll button clicked');

    // Try different selectors to find the PixelScroll component
    let pixelScrollElement = document.querySelector('.pixel-scroll-canvas-container');

    if (!pixelScrollElement) {
      console.log('Trying alternative selector...');
      pixelScrollElement = document.querySelector('.pixel-scroll-canvas');
    }

    if (!pixelScrollElement) {
      console.log('Trying to find any element with pixel-scroll in class name...');
      pixelScrollElement = document.querySelector('[class*="pixel-scroll"]');
    }

    console.log('Found element:', pixelScrollElement);

    if (pixelScrollElement) {
      console.log('PixelScroll element found, scrolling...');

      // Get the element's position relative to viewport and current scroll
      const rect = pixelScrollElement.getBoundingClientRect();
      const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const elementTop = rect.top + currentScrollTop;

      console.log('Element rect.top:', rect.top);
      console.log('Current scroll position:', currentScrollTop);
      console.log('Calculated element top:', elementTop);
      console.log('Window innerHeight:', window.innerHeight);

      // Calculate offset (100 pixels)
      const additionalOffset = 50;

      // Scroll to element position plus the offset
      const targetPosition = elementTop + additionalOffset;

      console.log('Additional offset:', additionalOffset);
      console.log('Target scroll position:', targetPosition);

      try {
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
        console.log('Scroll command executed');
      } catch (error) {
        console.error('Error during scroll:', error);
      }
    } else {
      console.log('PixelScroll element not found, using fallback');
      // Fallback: scroll to a specific position
      try {
        window.scrollTo({
          top: 800, // Adjust this value as needed
          behavior: 'smooth'
        });
        console.log('Fallback scroll executed');
      } catch (error) {
        console.error('Error during fallback scroll:', error);
      }
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const stepEl = entry.target;
          const line = stepEl.querySelector('.how-it-works-step-line');
          const bullet = stepEl.querySelector('.how-it-works-step-svg-bullet');
          const textEls = stepEl.querySelectorAll('.step-img, .step-text, .how-it-works-step-absolute-center-text');

          if (entry.isIntersecting) {
            bullet?.classList.add('is-visible');
            line?.classList.add('grow');

            // Add is-visible to child elements
            textEls.forEach((el) => {
              setTimeout(() => el.classList.add('is-visible'), 250);
            });

            // Don't add is-visible to grid-wrapper here (gridObserver handles that)

          } else {
            bullet?.classList.remove('is-visible');
            // line?.classList.remove('grow');

            // Remove is-visible from child elements
            textEls.forEach((el) => {
              el.classList.remove('is-visible');
            });

            // Don't remove is-visible from grid-wrapper here (gridObserver handles that)
          }
        });
      },
      {
        threshold: 0.75,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    // Separate observer for grid-wrapper with 0.4 threshold (only for is-visible class)
    const gridObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => entry.target.classList.add('is-visible'), 250);
          } else {
            entry.target.classList.remove('is-visible');
          }
        });
      },
      {
        threshold: 0.3,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    // Observer for pixel scroll lines
    const lineObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('grow');
          } else {
            entry.target.classList.remove('grow');
          }
        });
      },
      {
        threshold: 0.5,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    stepRefs.current.forEach((step) => {
      // Use both observers for grid-wrapper (main observer for line/bullet, gridObserver for is-visible timing)
      if (step.classList.contains('grid-wrapper')) {
        observer.observe(step);
        gridObserver.observe(step);
      } else {
        observer.observe(step);
      }
    });

    // Observe the pixel scroll lines
    let topLine, bottomLine;

    setTimeout(() => {
      topLine = document.querySelector('.pixel-scroll-line-top');
      bottomLine = document.querySelector('.pixel-scroll-line-bottom');

      console.log('Top line found:', topLine);
      console.log('Bottom line found:', bottomLine);

      // Animate top line immediately on page load
      if (topLine) {
        setTimeout(() => {
          console.log('Adding grow class to top line');
          topLine.classList.add('grow');
        }, 500); // Small delay for better visual effect
      } else {
        console.log('Top line not found!');
      }

      // Only observe bottom line for scroll animation
      if (bottomLine) {
        lineObserver.observe(bottomLine);
        console.log('Bottom line observer added');
      } else {
        console.log('Bottom line not found!');
      }
    }, 100); // Small delay to ensure DOM is ready

    return () => {
      stepRefs.current.forEach((step) => {
        if (step.classList.contains('grid-wrapper')) {
          observer.unobserve(step);
          gridObserver.unobserve(step);
        } else {
          observer.unobserve(step);
        }
      });

      if (topLine) lineObserver.unobserve(topLine);
      if (bottomLine) lineObserver.unobserve(bottomLine);

      observer.disconnect();
      gridObserver.disconnect();
      lineObserver.disconnect();
    };
  }, []);


  return (
    <>

      <section className="container-fluid">


        {/* <div className="py-4 py-lg-4 py-md-5"></div>
        <div className="py-4 py-lg-4 py-md-5"></div>
        <div className="container">
          <div className="row justify-content-start">
            <div className="col-12 col-md-10 offset-md-1">
              <h4 className="f-uc">
                How it works
              </h4>
            </div>
          </div>
        </div>

        <div className="container">
          <div className="row justify-content-start">
            <div className="col-12 col-md-6 offset-md-1 col-lg-7 offset-lg-1">
              <p className="fw-light mb-2">
                Getting started is easy.
              </p>
              <p className="fw-light text-muted">
                We handle everything.<br /> You decide what to do with it.
              </p>
            </div>
          </div>
        </div> */}

        {/* TOOLS Section 1 */}
        <section className="tools-section py-5" style={{ background: 'var(--c0, #fff)', minHeight: '100vh' }}>
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-4">
                <div className="tools-content">
                  <h2 className="f-a c-c00 fs-1 mb-4"><GlyphSvg glyph={'g04'} size={"32"} /> Avatar Generation</h2>
                  <p className="f-b c-c00 fs-6 mb-4">
                    Create stunning, high-quality avatars with our advanced AI-powered generation tools. 
                    Transform any image into a professional avatar in seconds.
                  </p>
                  <button className="btn o-c00 c-c00 f-a fs-6 px-4 py-2">
                    BOOK DEMO
                  </button>
                </div>
              </div>
              <div className="col-lg-8">
                <div className="tools-video-container">
                  <video 
                    className="tools-video"
                    autoPlay 
                    muted 
                    loop 
                    playsInline
                    style={{
                      width: '100%',
                      height: '80vh',
                      objectFit: 'contain',
                      borderRadius: '8px'
                    }}
                  >
                    <source src="/video/avatar_selection.webm" type="video/webm" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TOOLS Section 2 */}
        <section className="tools-section py-5" style={{ minHeight: '100vh' }}>
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-4">
                <div className="tools-content">
                  <h2 className="f-a c-c00 fs-1 mb-4"><GlyphSvg color={"var(--c00)"} glyph={'g06'} size={"32"} /> Pose Generation</h2>
                  <p className="f-b c-c00 fs-6 mb-4">
                    Generate endless, realistic poses of your photos with our advanced pose generation technology.
                  </p>
                  <button className="btn o-c00 c-c00 f-a fs-6 px-4 py-2">
                    BOOK DEMO
                  </button>
                </div>
              </div>
              <div className="col-lg-8">
                <div className="tools-video-container">
                  <video 
                    className="tools-video"
                    autoPlay 
                    muted 
                    loop 
                    playsInline
                    style={{
                      width: '100%',
                      height: '80vh',
                      objectFit: 'contain',
                      borderRadius: '8px'
                    }}
                  >
                    <source src="/video/pose_generation.webm" type="video/webm" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TOOLS Section 3 */}
        <section className="tools-section pt-5" style={{ background: 'var(--c0, #fff)', minHeight: '100vh' }}>
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-4">
                <div className="tools-content">
                  <h2 className="f-a c-c00 fs-1 mb-4">Flatlay Try-on</h2>
                  <p className="f-b c-c00 fs-6 mb-4">
                    Place product photos in a flatlay on an existing image.
                  </p>
                  <button className="btn o-c00 c-c00 f-a fs-6 px-4 py-2">
                    BOOK DEMO
                  </button>
                </div>
              </div>
              <div className="col-lg-8">
                <div className="tools-video-container">
                  <video 
                    className="tools-video"
                    autoPlay 
                    muted 
                    loop 
                    playsInline
                    style={{
                      width: '100%',
                      height: '80vh',
                      objectFit: 'contain',
                      borderRadius: '8px'
                    }}
                  >
                    <source src="/video/flatlay.webm" type="video/webm" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="justify-content-center">
          <div className="col-12">
            <div className="text-center how-it-works-first-step-center-text">
              <button
                onClick={scrollToPixelScroll}
                className="mb-2 px-3 py-2 f-uc shadow-sm fs-6 fw-light card w-fit-content mx-auto d-inline-block b-c0 c-c5 o-c5 border-0"
                style={{ cursor: 'pointer' }}
              >
                Start Iterating
              </button>

              <div className="line-container">
                <div className="pixel-scroll-line-top"></div>
              </div>

            </div>
            <PixelScroll />
          </div>
        </div>

        <div className="line-container">
          <div className="pixel-scroll-line-bottom"></div>
        </div>

        {/* Block 1: Text on Left, SVG on Right */}
        <div className="container">

          <div className="how-it-works-step" ref={addToRefs}> {/* Add ref={addToRefs} to each step */}
            <div className="how-it-works-step-left">
              <div className="text-end step-text"> {/* Text aligned right in left column */}
                <h4 className="fs-2 fw-light mb-2"><span className="o-c1 rounded p-1 fs-6 small c-c1 hiw-number f-b align-top me-1">01</span>Machine Learning</h4>
                <p className="fs-6 mb-2">
                  To match your look & feel, we train a custom generative model based on your existing photography style.
                </p>
                <p className="fs-6 text-muted mb-0">
                  Provide us with high resolution, existing product photos for the best results.
                </p>
              </div>
            </div>
            <div className="how-it-works-step-center">
              <div className="how-it-works-step-bullet"><GlyphSvg glyph={'g01'} size={"32"} color={"var(--c5)"} className="how-it-works-step-svg-bullet" /></div>
              <div className="how-it-works-step-line"></div>

            </div>
            <div className="how-it-works-step-right">
              <div className="text-start step-img responsive-step-media"> {/* Added responsive class */}
                <video 
                className='rounded'
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                  style={{ width: '100%', height: 'auto', margin: '0' }}
                >
                  <source src="/video/machine_learning-5.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                
                {/* VAR and IDX overlays */}
                <div style={{ position: 'absolute', bottom: 10, left: 12, zIndex: 100, display: 'flex', flexDirection: 'column', gap: 0 }}>
                  <span
                    style={{
                      fontFamily: 'var(--font-b)',
                      fontSize: '11px',
                      color: 'var(--c0)',
                      letterSpacing: '0.08em',
                      fontWeight: 500,
                      lineHeight: 1.1,
                      userSelect: 'none',
                      pointerEvents: 'none',
                    }}
                  >
                    var_serv
                  </span>
                  <span
                    style={{
                      fontFamily: 'var(--font-b)',
                      fontSize: '11px',
                      color: 'var(--c0)',
                      letterSpacing: '0.08em',
                      fontWeight: 500,
                      lineHeight: 1.1,
                      userSelect: 'none',
                      pointerEvents: 'none',
                    }}
                  >
                    idx_ml-5
                  </span>
                </div>

                {/* SVG glyph in top right */}
                <svg
                  width={12}
                  height={12}
                  viewBox="0 0 24 24"
                  style={{
                    position: 'absolute',
                    top: 10,
                    right: 12,
                    zIndex: 100,
                    display: 'block',
                    pointerEvents: 'none',
                  }}
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M.71,23.37V.79H23.29V23.37Zm7.52-7.52h7.54V8.31H8.23Z" fill="var(--c0)" />
                </svg>
              </div>
            </div>
          </div>
          {/* Block 2: SVG on Left, Text on Right */}
          <div className="how-it-works-step" ref={addToRefs}> {/* Add ref here too */}
            <div className="how-it-works-step-left">
              <div className="text-end step-img"> {/* SVG aligned right in left column */}
                <AnimatedGlyphGrid size={24} />
              </div>
            </div>
            <div className="how-it-works-step-center">
              <div className="how-it-works-step-bullet"><GlyphSvg glyph={'g02'} size={"32"} color={"var(--c5)"} className="how-it-works-step-svg-bullet" /></div>
              <div className="how-it-works-step-line"></div>
            </div>
            <div className="how-it-works-step-right">
              <div className="text-start step-text"> {/* Text aligned left in right column */}
                <h4 className="fs-2 fw-light mb-2"><span className="o-c1 rounded p-1 fs-6 small c-c1 hiw-number f-b align-top me-1">02</span>Avatar Selection</h4>
                <p className="fs-6 mb-2">
                  Choose from our growing assortment of digital avatars.
                </p>
                <p className="fs-6 text-muted mb-0">
                  Want an exclusive avatar? We can do that do. Working together we can develop custom avatars to meet your needs.
                </p>
              </div>
            </div>
          </div>
          {/* Block 3: Text on Left, SVG on Right */}
          <div className="how-it-works-step" ref={addToRefs}> {/* Add ref here too */}
            <div className="how-it-works-step-left">
              <div className="text-end step-text"> {/* Text aligned right in left column */}
                <h4 className="fs-2 fw-light mb-2"><span className="o-c1 rounded p-1 fs-6 small c-c1 hiw-number f-b align-top me-1">03</span>Select Critera</h4>
                <p className="fs-6 text-muted mb-2">
                  Choose which variety you want to see for image generation. Variables include expression, age, and ethnicity, etc.
                </p>
              </div>
            </div>
            <div className="how-it-works-step-center">
              <div className="how-it-works-step-bullet"><GlyphSvg glyph={'g03'} size={"32"} color={"var(--c5)"} className="how-it-works-step-svg-bullet" /></div>
              <div className="how-it-works-step-line"></div>
            </div>
            <div className="how-it-works-step-right">
              <div className="text-start step-img responsive-step-media"> {/* Added responsive class */}
                <ImageCrossfade />
              </div>
            </div>
          </div>
          {/* Block 4: SVG on Left, Text on Right */}
          <div className="how-it-works-step" ref={addToRefs}> {/* Add ref here too */}
            <div className="how-it-works-step-left">
              <div className="text-end step-img"> {/* SVG aligned right in left column */}
                <UploadProgressGlyphs />
              </div>
            </div>
            <div className="how-it-works-step-center">
              <div className="how-it-works-step-bullet"><GlyphSvg glyph={'g04'} size={"32"} color={"var(--c5)"} className="how-it-works-step-svg-bullet" /></div>
              <div className="how-it-works-step-line"></div>
            </div>
            <div className="how-it-works-step-right">
              <div className="text-start step-text"> {/* Text aligned left in right column */}
                <h4 className="fs-2 fw-light mb-2"><span className="o-c1 rounded p-1 fs-6 small c-c1 hiw-number f-b align-top me-1">04</span>Content Upload</h4>
                <p className="fs-6 text-muted mb-2">
                  Supply us with your base content and our algorithm will do the heavy lifting.
                </p>
              </div>
            </div>
          </div>
          {/* Centred Block - Add ref here too */}
          <div 
          style={{ minHeight: '50px', position: 'relative' }}  
          className="how-it-works-step how-it-works-step-centered-message" ref={addToRefs}>
            <div
              className="how-it-works-step-center">
              <div className="how-it-works-step-line"></div>
            </div>
            <div 
            style={{ top: '0px', position: 'absolute' }}
            className="how-it-works-step-absolute-center-text">
              <p className="mb-0 fs-6 fw-light text-center">
                We generate new, high quality assets to your chosen variables
              </p>
            </div>
          </div>

          {/* Last Block: Centered Content (with video) */}
          <div className="container my-5 grid-wrapper" ref={addToRefs}>
            <div className="row justify-content-center">
              <div className="col-12 col-md-10">
                <div className="rounded-4 shadow-sm mb-4">
                  <ImageGridCanvas />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="row justify-content-center b-c5 py-5">
          <div className="py-3"></div>
          <h1 className='text-center c-c0 mb-5'>
            Limitless iteration is at your fingertips.<br />
            Let's get started.
          </h1>
          <div className="d-flex flex-column gap-3 align-items-center">
            <a className="btn b-c0 c-c00 shadow" style={{ width: 'auto' }} href="/contact" data-discover="true">Let's Chat</a>
            <a className="btn b-c5 c-c0 border border-c0 shadow" style={{ width: 'auto' }} href="/pricing" data-discover="true">View Pricing</a>
            <div className="py-3"></div>
          </div>
        </div>
      </section>
    </>
  );
}